import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { BEAT_LABEL_NAMES, ReviewStatistics } from '../review';

const hours = (s?: number) => (s === undefined || s === null ? '—' : `${(s / 3600).toFixed(1)} h`);
const num = (v?: number | null, suffix = '') => (v === undefined || v === null ? '—' : `${v}${suffix}`);

/**
 * The report in progress (§7.3). Every figure comes from the server, derived from the engine's
 * output plus review state — nothing here is computed or typed in the browser.
 *
 * Collapsible, default open: it's a third full-height column next to the triage queue, worth
 * less space than the queue or the chart (§ layout, 2026-09-14). The per-class episode count and
 * reviewed/total that used to be here were dropped — the triage queue's own group headers already
 * show that; burden percent is the one figure here that lives nowhere else.
 */
export const StatsRail: React.FC<{ stats: ReviewStatistics; engineVersion: string }> = ({ stats, engineVersion }) => {
  const [open, setOpen] = useState(true);
  const classes = Object.keys(stats.verification).sort();
  return (
    <div className="glass-panel space-y-3 rounded-2xl p-4 text-xs">
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between">
        <span className="flex items-center gap-1.5 font-bold uppercase tracking-wide text-white">
          {open ? <ChevronDown className="h-3.5 w-3.5 text-slate-500" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-500" />}
          Statistics
        </span>
        <span className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold ${stats.fully_verified ? 'border-emerald-800 text-emerald-300' : 'border-amber-800 text-amber-300'}`}>
          {stats.fully_verified ? 'VERIFIED' : 'PENDING REVIEW'}
        </span>
      </button>
      {open && (
        <div className="space-y-4">
          <dl className="mono grid grid-cols-2 gap-x-3 gap-y-1 text-slate-300">
            <dt className="text-slate-500">Monitored</dt><dd className="text-right">{hours(stats.monitored_seconds)}</dd>
            <dt className="text-slate-500">Analyzable</dt>
            <dd className="text-right">{hours(stats.analyzable_seconds)}{stats.analyzable_fraction != null && ` (${(stats.analyzable_fraction * 100).toFixed(1)}%)`}</dd>
            {/* Both halves wrap as units. The label wrapping between its own slashes put a
                stray "/ max" on a line of its own next to a value that had wrapped separately,
                so the pair read as "HR min / mean / / max — 10 / 76.3 / 299" (2026-09-21). */}
            <dt className="whitespace-nowrap text-slate-500">HR min/mean/max</dt>
            <dd className="whitespace-nowrap text-right">{num(stats.hr_min)} / {num(stats.hr_mean)} / {num(stats.hr_max)}</dd>
            <dt className="text-slate-500">Beats</dt><dd className="text-right">{stats.total_beats?.toLocaleString() ?? "—"}</dd>
          </dl>
          {!stats.hr_recomputed_after_review && stats.clusters_relabelled > 0 && (
            <p className="text-[11px] text-amber-400">Heart-rate figures are the engine's and do not yet reflect the {stats.clusters_relabelled} relabelled cluster(s).</p>
          )}

          <div>
            <p className="mb-1 text-slate-500">Beat classes</p>
            {Object.entries(stats.beat_counts).sort((a, b) => b[1] - a[1]).map(([label, count]) => (
              <div key={label} className="mono flex justify-between text-slate-300">
                <span>{BEAT_LABEL_NAMES[label] ?? label}</span><span>{count.toLocaleString()}</span>
              </div>
            ))}
          </div>

          <div>
            <p className="mb-1 text-slate-500">Burden (rejected excluded, against analyzable time)</p>
            {classes.length === 0 && <p className="text-slate-400">None reported by the engine.</p>}
            {classes.map((cls) => {
              const pct = stats.burden_percent[cls];
              return (
                <div key={cls} className="mono flex items-center justify-between text-slate-300">
                  <span className="uppercase">{cls}</span>
                  <span>{pct == null ? '—' : `${pct.toFixed(2)}%`}</span>
                </div>
              );
            })}
          </div>
          <p className="text-[10px] text-slate-500">Engine {engineVersion}.</p>
        </div>
      )}
    </div>
  );
};
